import { apiUrl } from './apiUrl';
import { s3ThemeAssetUrl } from './themeAssets';

export const MESSAGE_MAX_LEN = 200;
export const POSTS_PER_PAGE = 8;
export const ALL_FILTER = 'ALL';
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Turn stored <br> tags into newlines so subtitles can use white-space: pre-line. */
export function formatGameSubtitle(text) {
  return String(text || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\r\n/g, '\n');
}

export const ACTIVE_GAMES = {
  CHEERS: 'cheers_to_peers',
  BADGES: 'badges_of_trust',
  MENS_DAY: 'mens_day',
  BOSS_COUNTS: 'every_boss_counts',
  FREEDOM: 'my_freedom',
};

export const GAME_CATALOG = [
  { id: ACTIVE_GAMES.CHEERS, name: 'Cheers to Peers', shortName: 'Cheers', description: 'Badge carousel, peer appreciation, gallery, and likes.' },
  { id: ACTIVE_GAMES.BADGES, name: 'Badges of Trust', shortName: 'Badges', description: 'Media uploads, category feed, and social appreciation.' },
  { id: ACTIVE_GAMES.MENS_DAY, name: "Men's Day", shortName: "Men's Day", description: "Men's Day badges, uploads, and social feed." },
  { id: ACTIVE_GAMES.BOSS_COUNTS, name: 'Every Boss Counts', shortName: 'Boss Counts', description: 'Flip-card boss archetypes, nominations, and social feed.' },
  { id: ACTIVE_GAMES.FREEDOM, name: 'My Freedom', shortName: 'Freedom', description: 'Freedom badges, story sharing, and social feed.' },
];

export function getGameDisplayName(activeGame) {
  const found = GAME_CATALOG.find((g) => g.id === activeGame);
  return found?.name || 'Cheers to Peers';
}

export const GAME_ICON_SIZES = {
  [ACTIVE_GAMES.CHEERS]: { width: 534, height: 534 },
  [ACTIVE_GAMES.BADGES]: { width: 534, height: 534 },
  [ACTIVE_GAMES.MENS_DAY]: { width: 534, height: 534 },
  [ACTIVE_GAMES.BOSS_COUNTS]: { width: 1012, height: 1012 },
  [ACTIVE_GAMES.FREEDOM]: { width: 270, height: 271 },
};

export const GAME_DB_TYPES = {
  [ACTIVE_GAMES.CHEERS]: 'cheers_badge',
  [ACTIVE_GAMES.BADGES]: 'badges_upload',
  [ACTIVE_GAMES.MENS_DAY]: 'mens_upload',
  [ACTIVE_GAMES.BOSS_COUNTS]: 'boss_nomination',
  [ACTIVE_GAMES.FREEDOM]: 'freedom_story',
};

export function activeGameToDbType(activeGame) {
  return GAME_DB_TYPES[activeGame] || GAME_DB_TYPES[ACTIVE_GAMES.CHEERS];
}

/** Canonical icon pixel size for the active game. */
export function getGameIconSize(activeGame) {
  return GAME_ICON_SIZES[activeGame] || GAME_ICON_SIZES[ACTIVE_GAMES.CHEERS];
}

export function getGameCustomizeMeta(activeGame) {
  switch (activeGame) {
    case ACTIVE_GAMES.BADGES:
    case ACTIVE_GAMES.MENS_DAY:
      return {
        pageTitle: 'Upload Categories & Images',
        pageSubtitle: 'Manage upload categories, category icons, and descriptions for this game.',
        categoryLabel: 'Category',
        iconLabel: 'Category icon',
        descriptionLabel: 'Category description',
        addTitleLabel: 'Add category',
      };
    case ACTIVE_GAMES.BOSS_COUNTS:
      return {
        pageTitle: 'Boss Archetypes & Images',
        pageSubtitle: 'Edit flip-card boss types shown to players — name, image, and back description.',
        categoryLabel: 'Archetype name',
        iconLabel: 'Archetype image',
        descriptionLabel: 'Flip-card description',
        addTitleLabel: 'Add archetype',
      };
    case ACTIVE_GAMES.FREEDOM:
      return {
        pageTitle: 'Freedom Badges & Images',
        pageSubtitle: 'Edit freedom badge types — name and image.',
        categoryLabel: 'Badge name',
        iconLabel: 'Badge image',
        descriptionLabel: 'Badge description',
        addTitleLabel: 'Add freedom badge',
      };
    default:
      return {
        pageTitle: 'Badge Categories & Images',
        pageSubtitle: 'Manage peer appreciation badges, icons, and messages.',
        categoryLabel: 'Badge name',
        iconLabel: 'Badge icon',
        descriptionLabel: 'Badge description',
        addTitleLabel: 'Add badge title',
      };
  }
}

/** Same-origin API proxy — use only when html2canvas / CORS needs it. */
export const badgeImageUrl = (file) => {
  if (!file) return '';
  const base = String(file).trim().split(/[/\\]/).pop();
  return base ? apiUrl(`/badgeImage?file=${encodeURIComponent(base)}`) : '';
};

const BUNDLED_GAME_ASSETS = /^(frdm[1-8]\.png|back\.png)$/i;

/**
 * Resolve badge / category / player-upload media for display.
 * Prefer public S3 (`REACT_APP_S3_PATH`) so Amplify does not depend on /api/badgeImage.
 */
export const resolveGameIconUrl = (file) => {
  if (!file) return '';
  const name = String(file).trim();
  if (!name) return '';
  if (/^https?:\/\//i.test(name)) return name;
  if (name.startsWith('/assets/games/')) return name;

  const base = name.includes('/') ? name.split(/[/\\]/).pop() : name;
  if (!base) return '';
  if (BUNDLED_GAME_ASSETS.test(base)) return `/assets/games/${base}`;

  const s3 = s3ThemeAssetUrl(base);
  if (s3) return s3;

  // Local / missing REACT_APP_S3_PATH — fall back to API proxy.
  return badgeImageUrl(base);
};

export const isTrue = (v) => String(v ?? '').trim().toLowerCase() === 'true';

export const firstEntry = (obj) => Object.entries(obj || {})[0] || [];

export const objArrToMap = (arr) =>
  (Array.isArray(arr) ? arr : []).reduce((acc, o) => {
    const [k, v] = firstEntry(o);
    if (k != null) acc[k] = v;
    return acc;
  }, {});

export const normalizeKey = (name) =>
  String(name || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export function buildCategories(themeData) {
  const names = Array.isArray(themeData?.default_category_name) ? themeData.default_category_name : [];
  const icons = Array.isArray(themeData?.category_icons) ? themeData.category_icons : [];
  const descMap = objArrToMap(themeData?.category_placeholder_text);
  const msgMap = objArrToMap(themeData?.category_message_text);

  return names
    .map((name, i) => {
      const [iconKey, iconVal] = firstEntry(icons[i]);
      const key = iconKey || normalizeKey(name);
      return {
        name: String(name || ''),
        key,
        icon: String(iconVal || ''),
        description: String(descMap[key] ?? ''),
        message: String(msgMap[key] ?? ''),
      };
    })
    .filter((c) => c.name && c.name.toUpperCase() !== ALL_FILTER);
}

/** Freedom badges only — never fall back to shared Men's Day / Badges category_icons. */
export function buildFreedomCategories(themeData) {
  const badges = Array.isArray(themeData?.freedom_badges) ? themeData.freedom_badges : [];
  if (badges.length > 0) {
    return badges
      .map((b) => ({
        name: String(b.name || ''),
        key: b.key || normalizeKey(b.name),
        icon: String(b.image || ''),
        description: String(b.description || ''),
        message: '',
      }))
      .filter((c) => c.name && c.name.toUpperCase() !== ALL_FILTER);
  }
  return [];
}

/** Boss archetypes only — never fall back to shared category_icons. */
export function buildBossCategories(themeData) {
  const cards = Array.isArray(themeData?.archetype_cards) ? themeData.archetype_cards : [];
  if (cards.length > 0) {
    return cards
      .map((c) => ({
        name: String(c.name || ''),
        key: c.key || normalizeKey(c.name),
        icon: String(c.image || ''),
        description: String(c.description || ''),
        message: '',
      }))
      .filter((c) => c.name && c.name.toUpperCase() !== ALL_FILTER);
  }
  return [];
}

export const PENDING_APPROVAL_MESSAGE =
  'Your post will display here after an admin approves it.';

export function dbTypeToActiveGame(gameType) {
  const entry = Object.entries(GAME_DB_TYPES).find(([, t]) => t === gameType);
  return entry ? entry[0] : '';
}

export function getActiveGame(themeData) {
  const raw = String(themeData?.active_game || ACTIVE_GAMES.CHEERS).trim();
  return Object.values(ACTIVE_GAMES).includes(raw) ? raw : ACTIVE_GAMES.CHEERS;
}

export async function apiPost(endpoint, token, body) {
  const resp = await fetch(apiUrl(endpoint), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const payload = await resp.json();
  return { ok: resp.ok, payload };
}
